import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import "./AnimatedLineChart.css";
import { useLanguage } from "../../../../Providers/LanguageContext";
import { useEffect, useState } from "react";
import { useUserData } from "../../../../Providers/UserWrapper";
import HttpClient from "../../../../utils/HttpClient";

export default function CurrentYearChart({
  data,
  time,
  userType,
  userCode,
  setSelectedOne,
  selectedYearFromDashboardPage,
}) {
  const { language } = useLanguage();
  const { isBusiness, isInvestor, isBookKeeper } = useUserData();
  const [formattedData, setFormattedData] = useState([]);
  const [yearsArray, setYearsArray] = useState([]);

  // State for date selections
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const cYear = new Date().getFullYear();
  useEffect(() => {
    const yArray = [];
    for (let year = cYear - 100; year <= cYear + 10; year++) {
      yArray.push(year);
    }
    setYearsArray(yArray);
  }, [cYear]);

  useEffect(() => {
    // console.log("selectedYearFromDashboardPage", selectedYearFromDashboardPage);
    setSelectedYear(selectedYearFromDashboardPage);
  }, [selectedYearFromDashboardPage, time]);

  const controlContainerStyle = {
    position: "absolute",
    top: "20px",
    right: "20px",
    zIndex: 10,
    backgroundColor: "white",
    padding: "8px 12px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    minWidth: "140px",
    textAlign: "center",
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  // Clear other selections when time changes
  useEffect(() => {
    setSelectedDate("");
    setSelectedWeek("");
    setSelectedMonth("");
    setSelectedYear("");
    // Clear parent selection when time changes
    setSelectedOne({ day: "", week: "", month: "", year: "" });
  }, [time]);

  // Update parent's selectedTwo whenever any selection changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedOne((prev) => ({
        day: selectedDate,
        week: "",
        month: "",
        year: "",
      }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedWeek) {
      setSelectedOne((prev) => ({
        day: "",
        week: selectedWeek,
        month: "",
        year: "",
      }));
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (selectedMonth) {
      setSelectedOne((prev) => ({
        day: "",
        week: "",
        month: selectedMonth,
        year: "",
      }));
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedYear) {
      console.log("true or false", selectedYearFromDashboardPage);
      setSelectedOne((prev) => ({
        day: "",
        week: "",
        month: "",
        // year: selectedYear,
        year: selectedYearFromDashboardPage?.toString(),
      }));
    }
  }, [selectedYear, selectedYearFromDashboardPage]);

  // Months array for month dropdown
  const months = [
    { month: "Jan", value: "1" },
    { month: "Feb", value: "2" },
    { month: "Mar", value: "3" },
    { month: "Apr", value: "4" },
    { month: "May", value: "5" },
    { month: "Jun", value: "6" },
    { month: "Jul", value: "7" },
    { month: "Aug", value: "8" },
    { month: "Sep", value: "9" },
    { month: "Oct", value: "10" },
    { month: "Nov", value: "11" },
    { month: "Dec", value: "12" },
  ];

  // Weeks array (1 to 52)
  const weeks = Array.from({ length: 52 }, (_, i) => ({
    week: `Week ${i + 1}`,
    value: (i + 1).toString(),
  }));

  // Years array for year selector (last 10 years + next 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) =>
    (currentYear - 10 + i).toString()
  );

  // Transform the data into the format needed for recharts
  useEffect(() => {
    if (data && data.length > 0) {
      const transformedData = transformDataForChart(data);
      setFormattedData(transformedData);
      console.log(
        "All labels:",
        transformedData.map((item) => item.label)
      );
    }
  }, [data, time]);

  // Function to transform API data into chart format - grouping by label
  const transformDataForChart = (apiData) => {
    // Group data by label (day/month/year)
    const groupedData = {};

    apiData.forEach((item) => {
      if (!groupedData[item.label]) {
        groupedData[item.label] = {
          label: item.label,
          revenue: null,
          expense: null,
          profit: null,
        };
      }

      // Set the value based on type
      if (item.type === "revenue") {
        groupedData[item.label].revenue = item.value;
      } else if (item.type === "expense") {
        groupedData[item.label].expense = item.value;
      } else if (item.type === "profit") {
        groupedData[item.label].profit = item.value;
      }
    });

    // Convert to array and sort based on time period
    const result = Object.values(groupedData);

    // Sort based on time period
    if (time === "week") {
      const dayOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      result.sort(
        (a, b) => dayOrder.indexOf(a.label) - dayOrder.indexOf(b.label)
      );
    } else if (time === "month") {
      const monthOrder = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      result.sort(
        (a, b) => monthOrder.indexOf(a.label) - monthOrder.indexOf(b.label)
      );
    } else if (time === "year") {
      // For years, sort numerically
      result.sort((a, b) => parseInt(a.label) - parseInt(b.label));
    }

    return result;
  };

  const [text, setText] = useState(() => {
    if (time === "day") {
      return language.current_day_total;
    } else if (time === "week") {
      return language.current_week_total;
    } else if (time === "month") {
      return language.current_month_total;
    } else if (time === "year") {
      return language.current_year_total;
    }
  });

  // Handle date change for day
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    // You can add API call here to fetch data for selected date
  };

  // Handle week change
  const handleWeekChange = (e) => {
    setSelectedWeek(e.target.value);
    // You can add API call here to fetch data for selected week
  };

  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    // You can add API call here to fetch data for selected month
  };

  // Handle year change
  const handleYearChange = (e) => {
    console.log("date format", e?.target?.value);
    setSelectedYear(e.target.value);
    // You can add API call here to fetch data for selected year
  };
  const handleYearClick = (year) => {
    console.log("date format", year);
    setSelectedYear(year.toString());
    setIsOpen(false);
    // setSelectedYear(toString(year));

    // handleYearChange(year);
  };

  // Render appropriate date selector based on time
  const renderDateSelector = () => {
    switch (time) {
      case "day":
        return (
          <div className="cyc-date-selector-container">
            {/* <label htmlFor="day-selector" className="cyc-date-selector-label">Select Date:</label> */}
            <input
              type="date"
              id="day-selector"
              value={selectedDate}
              onChange={handleDateChange}
              className="cyc-date-selector-input"
            />
          </div>
        );

      case "week":
        return (
          <div className="cyc-date-selector-container">
            {/* <label htmlFor="week-selector" className="cyc-date-selector-label">Select Week:</label> */}
            <select
              id="week-selector"
              value={selectedWeek}
              onChange={handleWeekChange}
              className="cyc-date-selector-input"
            >
              {weeks.map((week) => (
                <option key={week.value} value={week.value}>
                  {week.week}
                </option>
              ))}
            </select>
          </div>
        );

      case "month":
        return (
          <div className="cyc-date-selector-container">
            {/* <label htmlFor="month-selector" className="cyc-date-selector-label">Select Month:</label> */}
            <select
              id="month-selector"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="cyc-date-selector-input"
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.month}
                </option>
              ))}
            </select>
          </div>
        );

      // case "year":
      //   return (
      //     <div className="cyc-date-selector-container">
      //       {/* <label htmlFor="year-selector" className="cyc-date-selector-label">Select Year:</label> */}

      //       <select
      //         id="year-selector"
      //         value={selectedYear}
      //         onChange={handleYearChange}
      //         className="cyc-date-selector-input"
      //       >
      //         {years.map((year) => (
      //           <option key={year} value={year}>
      //             {year}
      //           </option>
      //         ))}
      //       </select>
      //     </div>

      //     // <div
      //     //   style={{
      //     //     ...controlContainerStyle,
      //     //     minWidth: "150px",
      //     //     cursor: "pointer",
      //     //     position: "relative",
      //     //     top: "-10px",
      //     //     right: "30px",
      //     //     width: "30%",
      //     //     marginTop: "50px",
      //     //     // backgroundColor: "black",
      //     //   }}
      //     // >
      //     //   <div
      //     //     onClick={toggleCalendar}
      //     //     style={{
      //     //       display: "flex",
      //     //       justifyContent: "space-between",
      //     //       alignItems: "center",
      //     //       padding: "4px 8px",
      //     //     }}
      //     //   >
      //     //     <span style={{ color: selectedYear ? "#102a3d" : "#999" }}>
      //     //       {selectedYear || "Select Year"}
      //     //     </span>
      //     //     <span style={{ fontSize: "12px", color: "#102a3d" }}>▼</span>
      //     //   </div>
      //     //   {isOpen && (
      //     //     <div
      //     //       style={{
      //     //         position: "absolute",
      //     //         top: "100%",
      //     //         left: 0,
      //     //         right: 0,
      //     //         backgroundColor: "white",
      //     //         border: "1px solid #e0e0e0",
      //     //         borderRadius: "8px",
      //     //         boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      //     //         maxHeight: "200px",
      //     //         overflowY: "auto",
      //     //         zIndex: 100,
      //     //         marginTop: "4px",
      //     //       }}
      //     //     >
      //     //       <div
      //     //         style={{
      //     //           padding: "8px",
      //     //           backgroundColor: "#f8f9fa",
      //     //           borderBottom: "1px solid #e0e0e0",
      //     //           position: "sticky",
      //     //           top: 0,
      //     //           zIndex: 1,
      //     //         }}
      //     //       >
      //     //         <input
      //     //           type="text"
      //     //           placeholder="Search year..."
      //     //           style={{
      //     //             width: "100%",
      //     //             padding: "4px 8px",
      //     //             border: "1px solid #ddd",
      //     //             borderRadius: "4px",
      //     //             fontSize: "12px",
      //     //           }}
      //     //           onChange={(e) => {
      //     //             const searchTerm = e.target.value;
      //     //             const filteredYears = yearsArray.filter((year) =>
      //     //               year.toString().includes(searchTerm)
      //     //             );
      //     //             setYearsArray(filteredYears);
      //     //           }}
      //     //         />
      //     //       </div>

      //     //       <div
      //     //         style={{
      //     //           maxHeight: "150px",
      //     //           overflowY: "auto",
      //     //         }}
      //     //       >
      //     //         {yearsArray?.map((year) => (
      //     //           <div
      //     //             key={year}
      //     //             onClick={() => handleYearClick(year)}
      //     //             // onClick={() => setSelectedYear(year)}
      //     //             style={{
      //     //               padding: "8px 12px",
      //     //               cursor: "pointer",
      //     //               borderBottom: "1px solid #f0f0f0",
      //     //               backgroundColor:
      //     //                 selectedYear === year.toString()
      //     //                   ? "#e3f2fd"
      //     //                   : "white",
      //     //               color:
      //     //                 selectedYear === year.toString()
      //     //                   ? "#1976d2"
      //     //                   : "#102a3d",
      //     //               fontSize: "14px",
      //     //               fontWeight:
      //     //                 selectedYear === year.toString() ? "600" : "400",
      //     //               "&:hover": {
      //     //                 backgroundColor: "#f5f5f5",
      //     //               },
      //     //             }}
      //     //             onMouseEnter={(e) =>
      //     //               (e.currentTarget.style.backgroundColor = "#f5f5f5")
      //     //             }
      //     //             onMouseLeave={(e) => {
      //     //               e.currentTarget.style.backgroundColor =
      //     //                 selectedYear === year.toString()
      //     //                   ? "#e3f2fd"
      //     //                   : "white";
      //     //             }}
      //     //           >
      //     //             {year}
      //     //           </div>
      //     //         ))}
      //     //       </div>
      //     //     </div>
      //     //   )}
      //     // </div>
      //   );

      default:
        return null;
    }
  };

  // Custom dot components for each line
  const RevenueDot = (props) => {
    const { cx, cy, value } = props;
    if (value === null || value === undefined) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="#aaa"
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  const ExpenseDot = (props) => {
    const { cx, cy, value } = props;
    if (value === null || value === undefined) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="#0f172a"
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  const ProfitDot = (props) => {
    const { cx, cy, value } = props;
    if (value === null || value === undefined) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="#334155"
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  // Custom active dots for hover
  const RevenueActiveDot = (props) => {
    const { cx, cy } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill="#aaa"
        stroke="white"
        strokeWidth={3}
      />
    );
  };

  const ExpenseActiveDot = (props) => {
    const { cx, cy } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill="#0f172a"
        stroke="white"
        strokeWidth={3}
      />
    );
  };

  const ProfitActiveDot = (props) => {
    const { cx, cy } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill="#334155"
        stroke="white"
        strokeWidth={3}
      />
    );
  };

  // Get max value for YAxis domain - FIXED
  const getMaxValue = () => {
    if (!formattedData || formattedData.length === 0) return 30000;

    let max = 0;

    formattedData.forEach((item) => {
      const revenue = item.revenue != null ? Number(item.revenue) : 0;
      const expense = item.expense != null ? Number(item.expense) : 0;
      const profit = item.profit != null ? Number(item.profit) : 0;

      max = Math.max(max, revenue, expense, profit);
    });

    // when everything is 0, keep a default so chart is still visible
    if (max === 0) {
      return 30000;
    }

    // Add 10% padding to the top for better visualization
    return max * 1.1;
  };

  // Generate YAxis ticks based on max value - IMPROVED
  const generateYTicks = () => {
    const max = getMaxValue();

    // same fallback ticks when we use the 30000 default
    if (max === 30000) {
      return [0, 5000, 10000, 15000, 20000, 25000, 30000];
    }

    // Create 6 ticks including 0 and max (5 intervals)
    const intervals = 5;
    const step = max / intervals;

    const ticks = [];
    for (let i = 0; i <= intervals; i++) {
      const value = Math.round(step * i);
      // Ensure the last tick is exactly max (avoid rounding drift)
      ticks.push(i === intervals ? max : value);
    }

    return ticks;
  };

  const [hoverData, setHoverData] = useState({
    revenue: {},
    expense: {},
    profit: {},
  });

  // getting hover data
  const getHoverData = async () => {
    const data = {
      dayType: time,
    };

    // revenue hover
    const res = await HttpClient.requestData(
      isInvestor || isBookKeeper
        ? `investor/dashboard-one-hover-revenue/${userCode}`
        : isBusiness
        ? "dashboard-one-hover-revenue"
        : "dashboard-one-hover-revenue-pu",
      "POST",
      data
    );
    if (res && res?.status) {
      setHoverData((prev) => ({ ...prev, revenue: res?.data }));
    }

    // expense hover
    const res1 = await HttpClient.requestData(
      isInvestor || isBookKeeper
        ? `investor/dashboard-one-hover-expense/${userCode}`
        : "dashboard-one-hover-expense",
      "POST",
      data
    );
    if (res1 && res1?.status) {
      setHoverData((prev) => ({ ...prev, expense: res1?.data }));
    }

    // profit hover
    const res2 = await HttpClient.requestData(
      isInvestor || isBookKeeper
        ? `investor/dashboard-one-hover-profit/${userCode}`
        : isBusiness
        ? "dashboard-one-hover-profit"
        : "dashboard-one-hover-profit-pu",
      "POST",
      data
    );
    if (res2 && res2?.status) {
      setHoverData((prev) => ({ ...prev, profit: res2?.data }));
    }
  };

  // getting the day or week or month or year
  useEffect(() => {
    getHoverData();
  }, [time, userCode]);

  // Update text based on time
  useEffect(() => {
    if (time === "day") {
      setText(language.current_day_total || "Today's Total");
    } else if (time === "week") {
      setText(language.current_week_total || "This Week's Total");
    } else if (time === "month") {
      setText(language.current_month_total || "This Month's Total");
    } else if (time === "year") {
      setText(language.current_year_total || "This Year's Total");
    }
  }, [time, language]);

  // Format tick values for YAxis - ADDED FOR BETTER NUMBER FORMATTING
  // const formatYTick = (value) => {
  //   if (value >= 1000000) {
  //     return `$${(value / 1000000).toFixed(1)}M`;
  //   } else if (value >= 1000) {
  //     return `$${(value / 1000).toFixed(1)}K`;
  //   } else {
  //     return `$${value}`;
  //   }
  // };
  const formatYTick = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 100000) {
      return `$${(value / 1000).toFixed(0)}K`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    } else {
      return `$${value}`;
    }
  };

  console.log("formattedData: >>>> ", formattedData);

  // Custom Tooltip Component to handle null/undefined data
  const CustomTooltip = ({ active, payload, label, hoverData, time }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold", marginBottom: "8px" }}>
          {time === "day"
            ? `Day: ${label}`
            : time === "week"
            ? `Week: ${label}`
            : time === "month"
            ? `Month: ${label}`
            : `Year: ${label}`}
        </p>

        {payload.map((entry, index) => {
          // Get the actual data point from formattedData
          const dataPoint = formattedData.find((item) => item.label === label);
          let value = 0;
          let hoverInfo = {};

          if (dataPoint) {
            value = dataPoint[entry.dataKey] || 0;

            // Get hover data for this metric
            if (entry.dataKey === "revenue" && hoverData.revenue) {
              hoverInfo = hoverData.revenue[label] || {};
            } else if (entry.dataKey === "expense" && hoverData.expense) {
              hoverInfo = hoverData.expense[label] || {};
            } else if (entry.dataKey === "profit" && hoverData.profit) {
              hoverInfo = hoverData.profit[label] || {};
            }
          }

          return (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "4px 0",
                color: entry.color,
                minWidth: "200px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: entry.color,
                    marginRight: "8px",
                  }}
                ></div>
                <span style={{ textTransform: "capitalize" }}>
                  {entry.dataKey}:
                </span>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: "bold" }}>
                  $
                  {value !== null && value !== undefined
                    ? value.toLocaleString()
                    : "0"}
                </div>
                {Object.keys(hoverInfo).length > 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {hoverInfo.description || ""}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="chart-card-ln1">
      <div className="chart-header-ln1">
        <h2 className="chart-title-ln1">{text}</h2>
        <div className="cyc-date-selector-wrapper">{renderDateSelector()}</div>
      </div>

      <div className="chart-wrapper-ln1">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={formattedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 25 }} // Increased bottom margin
          >
            {/* only horizontal dashed lines */}
            <CartesianGrid
              vertical={false}
              stroke="#94a3b8"
              strokeDasharray="4 4"
            />

            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => value}
              interval={0} // Force all labels to show
              padding={{ left: 10, right: 10 }} // Add padding
            />
            {/* <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, getMaxValue()]}
              ticks={generateYTicks()}
              tickFormatter={formatYTick} // Use the new formatter
            /> */}
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, getMaxValue()]}
              ticks={generateYTicks()}
              tickFormatter={formatYTick}
              allowDataOverflow={false}
            />

            {/* Revenue Line */}
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#aaa"
              strokeWidth={3}
              dot={<RevenueDot />}
              activeDot={<RevenueActiveDot />}
              connectNulls={true}
            />

            {/* Expense Line */}
            <Line
              type="monotone"
              dataKey="expense"
              stroke="#0f172a"
              strokeWidth={3}
              dot={<ExpenseDot />}
              activeDot={<ExpenseActiveDot />}
              connectNulls={true}
            />

            {/* Profit Line */}
            <Line
              type="monotone"
              dataKey="profit"
              stroke="#334155"
              strokeWidth={3}
              dot={<ProfitDot />}
              activeDot={<ProfitActiveDot />}
              connectNulls={true}
            />
            <Tooltip
              content={<CustomTooltip hoverData={hoverData} time={time} />}
              cursor={{ strokeDasharray: "3 3" }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              wrapperStyle={{ paddingBottom: "20px" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend-ln1">
        <div>
          <span className="dot revenue-ln1"></span> Revenue
        </div>
        <div>
          <span className="dot expense-ln1"></span> Expense
        </div>
        <div>
          <span className="dot profit-ln1"></span> Profit
        </div>
      </div>
    </div>
  );
}
